"use client";

import React, { useState } from "react";
import { useDispatch } from "react-redux";

//Styles
import * as S from "./styles";

//Type
import { FilterCommission, TabDownloadCommissionProps } from "@/types";

//Components
import {
  BoxAlignItems,
  BoxColumns,
  CardStatistics,
  DataTable,
} from "@/components";
import { FormGroup, Input, Select } from "@/components/form";
import LabelInput from "@/components/label";
import { ButtonComponent } from "@/components/notfication/styles";
import SmallTitleModal from "@/components/smallTitleModal";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import {
  useEmployee,
  useNormalCommission,
  usePaymentMethod,
  useSubscriptionCommission,
  useUser,
} from "@/hooks";
import useSettleCommissions from "@/hooks/useSettleCommissions";
import useAdvancePayment from "@/hooks/advancePayment/useAdvancePayment";
import {
  discountVouchers,
  fetchAdvancePayment,
} from "@/store/modules/advancePayment/thunks";
import { fetchFullCommission } from "@/store/modules/fullCommission/thunks";
import { formatCurrency } from "@/utils/formatCurrency";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, Search } from "lucide-react";
import { toast } from "react-toastify";

const TabDownloadCommission: React.FC<TabDownloadCommissionProps> = ({
  dict,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const { user, isLoading, isAuthenticated } = useUser();
  const { establishment } = useAuth();

  const [searchCommission, setSearchCommission] = useState<FilterCommission>({
    startDate: null,
    endDate: null,
    employee_id: null,
  });

  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [lastSearchFilters, setLastSearchFilters] =
    useState<FilterCommission | null>(null);

  const {
    subscriptionCommission,
    loading: loadingSubscriptionCommission,
    error: errorSubscriptionCommission,
    handleSubmitFilter: handleSubmitFilterSubscriptionCommission,
    currency: currencySubscriptionCommission,
    register: registerSubscriptionCommission,
    handleSubmit: handleSubmitSubscriptionCommission,
    reset: resetSubscriptionCommission,
    control: controlSubscriptionCommission,
    errors: errorsSubscriptionCommission,
    isSubmitting: isSubmittingSubscriptionCommission,
    onSubmitSubscriptionCommission,
    searchDataSubscriptionCommission,
    hasSearched: hasSearchedSubscriptionCommission,
    setIsSettlement: setSubmitTypeSubscriptionCommission,
    totalItemsUsageSubscription,
    itemsPerPagerUsageSubscription,
    totalPagesUsageSubscription,
    currentPagerPagesUsageSubscription,
    handlePageChangePagesUsageSubscription,
    // helpers
    getValues: getValuesSubscriptionCommission,
    setValue: setValueSubscriptionCommission,
    setError: setErrorSubscriptionCommission,
    clearErrors: clearErrorsSubscriptionCommission,
  } = useSubscriptionCommission({ filter: searchCommission, onSuccess });

  const {
    normalCommission,
    loading: loadingNormalCommission,
    error: errorNormalCommission,
    handleSubmitFilter: handleSubmitFilterNormalCommission,
    currency: currencyNormalCommission,
    register: registerNormalCommission,
    handleSubmit: handleSubmitNormalCommission,
    reset: resetNormalCommission,
    control: controlNormalCommission,
    getValues: getValuesNormalCommission,
    setError: setErrorNormalCommission,
    errors: errorsNormalCommission,
    isSubmitting: isSubmittingNormalCommission,
    currentPagerPurchaseVouchers,
    handlePageChangePurchaseVouchers,
    itemsPerPagerPurchaseVouchers,
    totalPagesrPurchaseVouchers,
    itemsPerPagerPendingNormalCommission,
    totalPagesrPendingNormalCommission,
    currentPagerPendingNormalCommission,
    handlePageChangePendingNormalCommission,
    onSubmitNormalCommission,
    hasSearched,
    setIsSettlement: setSubmitTypeNormalCommission,
  } = useNormalCommission({ filter: searchCommission, onSuccess });

  const handleNormalCommissionSearch = (data: any) => {
    setLastSearchFilters({
      startDate: data.startDate,
      endDate: data.endDate,
      employee_id: data.employee_id,
    });

    onSubmitNormalCommission(data);
  };

  const { employees } = useEmployee();

  const { paymentMethods } = usePaymentMethod();

  const { settleNormalCommission, settleSubscriptionCommission } =
    useSettleCommissions();

  const [settleMode, setSettleMode] = useState<
    "normal" | "subscription" | null
  >(null);
  const [isSettling, setIsSettling] = useState(false);

  const { advancePayment } = useAdvancePayment({
    dateRange: {
      start: lastSearchFilters?.startDate
        ? new Date(lastSearchFilters.startDate)
        : new Date(),
      end: lastSearchFilters?.endDate
        ? new Date(lastSearchFilters.endDate)
        : new Date(),
    },
  });

  const calculatePendingVouchers = () => {
    if (
      !hasSearched ||
      !lastSearchFilters ||
      !lastSearchFilters.employee_id ||
      !lastSearchFilters.startDate ||
      !lastSearchFilters.endDate
    ) {
      return 0;
    }

    const startDate = new Date(lastSearchFilters.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(lastSearchFilters.endDate);
    endDate.setHours(23, 59, 59, 999);

    const pendingVouchers = Array.isArray(advancePayment?.data)
      ? advancePayment.data.filter((voucher: any) => {
          if (
            voucher.user_id !== parseInt(lastSearchFilters.employee_id || "0")
          ) {
            return false;
          }

          if (voucher.was_discounted !== 0) {
            return false;
          }

          const createdAt = new Date(voucher.created_at);
          return createdAt >= startDate && createdAt <= endDate;
        })
      : [];

    return pendingVouchers.reduce((total: number, voucher: any) => {
      return total + parseFloat(voucher.amount || 0);
    }, 0);
  };

  const getFilteredVouchersForTable = () => {
    if (
      !hasSearched ||
      !lastSearchFilters ||
      !lastSearchFilters.employee_id ||
      !lastSearchFilters.startDate ||
      !lastSearchFilters.endDate
    ) {
      return [];
    }

    const startDate = new Date(lastSearchFilters.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(lastSearchFilters.endDate);
    endDate.setHours(23, 59, 59, 999);

    const pendingVouchers = Array.isArray(advancePayment?.data)
      ? advancePayment.data.filter((voucher: any) => {
          if (
            voucher.user_id !== parseInt(lastSearchFilters.employee_id || "0")
          ) {
            return false;
          }

          if (voucher.was_discounted !== 0) {
            return false;
          }

          const createdAt = new Date(voucher.created_at);
          return createdAt >= startDate && createdAt <= endDate;
        })
      : [];

    const vouchersForTable = pendingVouchers.map((voucher: any) => ({
      date: new Date(voucher.created_at).toLocaleDateString("pt-BR"),
      description: `Vale - ${voucher.description || "Adiantamento"}`,
      value: voucher.amount,
    }));

    const purchasesData = normalCommission?.data?.employee_debits?.data || [];

    const safePurchasesData = Array.isArray(purchasesData) ? purchasesData : [];

    return [...vouchersForTable, ...safePurchasesData];
  };

  const pendingVouchersTotal = calculatePendingVouchers();

  const totalPendingNormalCommission = normalCommission?.data?.total_to_receive;

  const calculateOutstandingPaymentToEmployee = () => {
    const totalCommissionsPending = Number(
      totalPendingNormalCommission?.total_commissions_pending ?? 0,
    );
    return totalCommissionsPending - pendingVouchersTotal;
  };

  const outstandingPaymentToEmployeeTotal =
    calculateOutstandingPaymentToEmployee();

  const generateMonthOptions = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const months = [];

    for (let i = 0; i <= 11; i++) {
      const date = new Date(currentYear, currentDate.getMonth() - i, 1);
      const monthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const monthLabel = date.toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric",
      });
      months.push({
        value: monthValue,
        label: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
      });
    }

    return [
      { value: "", label: dict["Select the month"] || "Selecione o mês" },
      ...months,
    ];
  };

  const getMonthDateRange = (monthValue: string) => {
    if (!monthValue) return { startDate: null, endDate: null };

    const [year, month] = monthValue.split("-");
    const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = new Date(parseInt(year), parseInt(month), 0); // Último dia do mês

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const monthOptions = generateMonthOptions();

  const focusField = (name: string) => {
    const el = document.querySelector<HTMLElement>(`[name="${name}"]`);
    if (el && typeof el.focus === "function") el.focus();
  };

  const handleSubscriptionCommissionSearch = () => {
    const monthDateRange = getMonthDateRange(selectedMonth);

    if (!monthDateRange.startDate || !monthDateRange.endDate) {
      setErrorSubscriptionCommission("startDate", {
        type: "manual",
        message: "Por favor, selecione um mês",
      } as any);
      focusField("selectedMonth");
      return;
    }

    // Get the employee value from the form
    const employeeId = getValuesSubscriptionCommission
      ? getValuesSubscriptionCommission("employee_id")
      : undefined;

    if (!employeeId) {
      setErrorSubscriptionCommission("employee_id", {
        type: "manual",
        message: "Por favor, selecione um funcionário",
      } as any);
      focusField("employee_id");
      return;
    }

    const searchData = {
      startDate: new Date(monthDateRange.startDate),
      endDate: new Date(monthDateRange.endDate),
      employee_id: employeeId,
    };

    // Explicitly request a search (not a settlement) to avoid race on isSettlement state
    onSubmitSubscriptionCommission(searchData, false);
  };

  const handleLocalSubscriptionSubmit = async (
    data: any,
    isSettlement: boolean = false,
  ) => {
    if (isSettlement) {
      setIsSettling(true);
      try {
        // determine start/end date: prefer selectedMonth -> then last search -> otherwise abort
        let startDateStr: string | null = null;
        let endDateStr: string | null = null;

        if (selectedMonth) {
          const monthRange = getMonthDateRange(selectedMonth);
          startDateStr = monthRange.startDate;
          endDateStr = monthRange.endDate;
        } else if (
          searchDataSubscriptionCommission?.startDate &&
          searchDataSubscriptionCommission?.endDate
        ) {
          startDateStr = new Date(searchDataSubscriptionCommission.startDate)
            .toISOString()
            .split("T")[0];
          endDateStr = new Date(searchDataSubscriptionCommission.endDate)
            .toISOString()
            .split("T")[0];
        }

        if (!startDateStr || !endDateStr) {
          setErrorSubscriptionCommission("startDate", {
            type: "manual",
            message:
              "Por favor, selecione um mês e um funcionário antes de dar baixa nas comissões de assinatura.",
          } as any);
          setErrorSubscriptionCommission("employee_id", {
            type: "manual",
            message: "Por favor, selecione um funcionário",
          } as any);
          // foco no select de mês, que fica acima do modal
          focusField("selectedMonth");
          return;
        }

        if (!data.payment_method_id) {
          setErrorSubscriptionCommission("payment_method_id", {
            type: "manual",
            message: "Por favor, selecione o método de pagamento",
          } as any);
          focusField("payment_method_id");
          return;
        }

        if (!data.paid_at) {
          setErrorSubscriptionCommission("paid_at", {
            type: "manual",
            message: "Por favor, preencha a data de pagamento",
          } as any);
          focusField("paid_at");
          return;
        }

        const payload: any = {
          start_date: startDateStr,
          end_date: endDateStr,
          employee_id: data.employee_id,
          paid_at:
            typeof data.paid_at === "string"
              ? data.paid_at
              : data.paid_at?.toISOString().split("T")[0],
          payment_method_id: data.payment_method_id,
        };

        toast.info("Enviando requisição de baixa de assinaturas...");
        await settleSubscriptionCommission(payload, {
          autoCreate: true,
          establishmentSlug: establishment?.slug,
          establishmentId: establishment?.id,
        });
        resetSubscriptionCommission();
        // refresh global full-commission report so UI reflects the settlement
        try {
          const fullCommissionParams: any = {
            filter: {
              establishment_id: establishment?.id,
            },
            start_date: startDateStr,
            end_date: endDateStr,
          };
          dispatch(fetchFullCommission(fullCommissionParams) as any);
        } catch (e) {
          // ignore
        }
        if (onSuccess) onSuccess();
      } catch (err) {
        // errors handled inside hook
      } finally {
        setIsSettling(false);
        setSettleMode(null);
      }
    } else {
      // search
      onSubmitSubscriptionCommission(data);
    }
  };

  const handleDiscountVouchers = async () => {
    if (
      !lastSearchFilters ||
      !lastSearchFilters.employee_id ||
      !lastSearchFilters.startDate ||
      !lastSearchFilters.endDate
    ) {
      return;
    }

    const formData = getValuesNormalCommission();

    if (!formData.paid_at) {
      setErrorNormalCommission("paid_at", {
        type: "manual",
        message: "Por favor, preencha a data de pagamento",
      } as any);
      focusField("paid_at");
      return;
    }

    if (!formData.payment_method_id) {
      setErrorNormalCommission("payment_method_id", {
        type: "manual",
        message: "Por favor, selecione o método de pagamento",
      } as any);
      focusField("payment_method_id");
      return;
    }

    try {
      const params: any = {
        userId: parseInt(lastSearchFilters.employee_id),
        startDate: new Date(lastSearchFilters.startDate)
          .toISOString()
          .split("T")[0],
        endDate: new Date(lastSearchFilters.endDate)
          .toISOString()
          .split("T")[0],
        paid_at:
          typeof formData.paid_at === "string"
            ? formData.paid_at
            : formData.paid_at.toISOString().split("T")[0],
        payment_method_id: String(formData.payment_method_id),
      };

      if (establishment?.id) {
        params.establishment_id = establishment.id;
      }

      await dispatch(discountVouchers(params) as any);

      // Close the settlement modal (if open)
      setSettleMode(null);

      // Refresh normal commission list (current tab) so UI reflects settlements
      const searchData = {
        startDate: lastSearchFilters.startDate,
        endDate: lastSearchFilters.endDate,
        employee_id: lastSearchFilters.employee_id,
      };
      onSubmitNormalCommission(searchData);

      // Refresh advance payments (vouchers)
      const valeParams = {
        filter: {
          start_date: new Date(lastSearchFilters.startDate)
            .toISOString()
            .split("T")[0],
          end_date: new Date(lastSearchFilters.endDate)
            .toISOString()
            .split("T")[0],
        },
      };
      dispatch(fetchAdvancePayment(valeParams) as any);

      // Also refresh the Full Commission report/list so the global view is up to date
      const fullCommissionParams: any = {
        filter: {
          establishment_id: establishment?.id,
        },
        start_date: new Date(lastSearchFilters.startDate)
          .toISOString()
          .split("T")[0],
        end_date: new Date(lastSearchFilters.endDate)
          .toISOString()
          .split("T")[0],
      };

      dispatch(fetchFullCommission(fullCommissionParams) as any);
    } catch (error) {
      console.error("Erro ao dar baixa nos vales:", error);
    }
  };

  const handleLocalNormalSubmit = async (
    data: any,
    isSettlement: boolean = false,
  ) => {
    if (isSettlement) {
      setIsSettling(true);
      try {
        const payload: any = {
          start_date: data.startDate.toISOString().split("T")[0],
          end_date: data.endDate.toISOString().split("T")[0],
          employee_id: data.employee_id,
          paid_at: data.paid_at
            ? data.paid_at.toISOString().split("T")[0]
            : null,
          payment_method_id: data.payment_method_id,
        };

        await settleNormalCommission(payload, {
          autoCreate: true,
          establishmentSlug: establishment?.slug,
          establishmentId: establishment?.id,
        });
        resetNormalCommission();
        // refresh global full-commission report so UI reflects the settlement
        try {
          const fullCommissionParams: any = {
            filter: {
              establishment_id: establishment?.id,
            },
            start_date: data.startDate.toISOString().split("T")[0],
            end_date: data.endDate.toISOString().split("T")[0],
          };
          dispatch(fetchFullCommission(fullCommissionParams) as any);
        } catch (e) {
          // ignore
        }
        if (onSuccess) onSuccess();
      } catch (err) {
        // handled inside hook
      } finally {
        setIsSettling(false);
        setSettleMode(null);
      }
    } else {
      onSubmitNormalCommission(data);
    }
  };

  const registerPendingNormalCommission = Array.isArray(
    normalCommission?.data?.commissions?.data,
  )
    ? normalCommission.data.commissions.data
    : [];
  const registerPurchaseVouchersNC = getFilteredVouchersForTable();

  const totalSubscriptionCommissions =
    subscriptionCommission?.data?.statistics?.raw_values?.total_commissions ||
    0;
  const totalSubscriptionCommissionsPaid =
    subscriptionCommission?.data?.statistics?.raw_values?.paid_commissions || 0;
  const totalSubscriptionCommissionsPending =
    subscriptionCommission?.data?.statistics?.raw_values?.pending_commissions ||
    0;
  const registerUsageSubscriptionCommissions = Array.isArray(
    subscriptionCommission?.data?.usages?.data,
  )
    ? subscriptionCommission.data.usages.data
    : [];

  const paymentMethodOptions = [
    { value: "", label: dict["Select a payment method"] },
    ...(Array.isArray(paymentMethods?.data)
      ? paymentMethods.data.map((method: any, index: number) => ({
          value: method.id,
          label: method.name,
        }))
      : []),
  ];

  const EmployeeOptions = [
    { value: "", label: dict["Select the employee"] },
    ...(Array.isArray(employees?.data)
      ? employees.data.map((employee: any, index: number) => ({
          value: employee.id,
          label: employee.name,
        }))
      : []),
  ];

  const UsageSubscription: ColumnDef<
    (typeof registerUsageSubscriptionCommissions)[0]
  >[] = [
    {
      accessorKey: "date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Date}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => row.getValue("date"),
      enableColumnFilter: true,
    },
    {
      accessorKey: "subscription_name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Signature}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => row.getValue("subscription_name"),
      enableColumnFilter: true,
    },
    {
      accessorKey: "service_name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Service}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => row.getValue("service_name"),
      enableColumnFilter: true,
    },
    {
      accessorKey: "points",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Points}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => `+${row.getValue("points")}`,
      enableColumnFilter: true,
    },
    {
      accessorKey: "client_name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Clients}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => row.getValue("client_name"),
      enableColumnFilter: true,
    },
  ];

  const columnsDataPurchaseVouchers: ColumnDef<
    (typeof registerPurchaseVouchersNC)[0]
  >[] = [
    {
      accessorKey: "date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Date}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => row.getValue("date"),
      enableColumnFilter: true,
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Description}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => {
        const rawDescription = row.getValue("description") as string;

        const [rawKey, rest] = rawDescription.split(" - ");

        const translatedKey = dict[rawKey] ?? rawKey;

        return <>{`${translatedKey} - ${rest}`}</>;
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: "value",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Value}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => {
        const value = Number(row.getValue("value"));
        return <>{formatCurrency(value, currencyNormalCommission)}</>;
      },
      enableColumnFilter: true,
    },
  ];

  const columnsDataPendingNormalCommission: ColumnDef<
    (typeof registerPendingNormalCommission)[0]
  >[] = [
    {
      accessorKey: "date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Date}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => row.getValue("date"),
      enableColumnFilter: true,
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Description}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => {
        const rawDescription = row.getValue("description");
        return <>{`${dict.Commission} - ${rawDescription}`}</>;
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: "value",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {dict.Value}
          <ArrowUpDown />
        </Button>
      ),
      cell: ({ row }) => {
        const value = Number(row.getValue("value"));
        return <>{formatCurrency(value, currencyNormalCommission)}</>;
      },
      enableColumnFilter: true,
    },
  ];

  return (
    <S.TabContainer defaultValue="normal-service" className="w-[400px]">
      <S.ListTabs>
        <S.TabButton value="normal-service">
          {dict["Normal Services"]}
        </S.TabButton>
        <S.TabButton value="signature">{dict.Signature}</S.TabButton>
      </S.ListTabs>
      <TabsContent value="signature">
        <div className="space-y-4">
          <FormGroup>
            <LabelInput labelInput={dict.Month || "Mês"} />
            <Select
              name="selectedMonth"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                clearErrorsSubscriptionCommission?.("startDate");
              }}
              options={monthOptions}
            />
            {errorsSubscriptionCommission?.startDate && (
              <p className="text-red-500 text-sm">
                {errorsSubscriptionCommission.startDate.message}
              </p>
            )}
          </FormGroup>
          <FormGroup>
            <LabelInput labelInput={dict.Employee} />
            <Select
              {...registerSubscriptionCommission("employee_id")}
              options={EmployeeOptions}
            />
            {errorsSubscriptionCommission.employee_id && (
              <p className="text-red-500 text-sm">
                {errorsSubscriptionCommission.employee_id.message}
              </p>
            )}
          </FormGroup>
          <ButtonComponent
            icon={<Search />}
            text={dict["Search"]}
            onClick={() => {
              setSubmitTypeSubscriptionCommission(false);
              handleSubscriptionCommissionSearch();
            }}
            type="button"
            widthFull
            variant="success"
          />
          <form
            onSubmit={handleSubmitSubscriptionCommission(
              (data: any, event?: any) => {
                // Detect which button triggered the submit using the native submitter
                const submitter = (event?.nativeEvent as SubmitEvent)
                  ?.submitter as HTMLElement | null;
                const action = submitter?.getAttribute("data-action");
                const isSettlement = action === "settlement";
                if (isSettlement) {
                  setSubmitTypeSubscriptionCommission(true);
                  setSettleMode("subscription");
                } else {
                  setSubmitTypeSubscriptionCommission(false);
                }

                return handleLocalSubscriptionSubmit(data, !!isSettlement);
              },
            )}
          >
            <FormGroup>
              <Input
                {...registerSubscriptionCommission("paid_at")}
                name="paid_at"
                type="date"
                label={dict["Payment Date"]}
              />
              {errorsSubscriptionCommission.paid_at && (
                <p className="text-red-500 text-sm">
                  {errorsSubscriptionCommission.paid_at.message}
                </p>
              )}
            </FormGroup>
            <FormGroup>
              <LabelInput labelInput={dict["Payment method"]} />
              <Select
                {...registerSubscriptionCommission("payment_method_id")}
                options={paymentMethodOptions}
              />
              {errorsSubscriptionCommission.payment_method_id && (
                <p className="text-red-500 text-sm">
                  {errorsSubscriptionCommission.payment_method_id.message}
                </p>
              )}
            </FormGroup>
            {hasSearchedSubscriptionCommission && (
              <>
                <FormGroup>
                  <SmallTitleModal Title={dict.Detailing} />
                  <BoxColumns>
                    <CardStatistics
                      title={dict["Total Commissions"]}
                      value={formatCurrency(
                        Number(totalSubscriptionCommissions ?? 0),
                        currencySubscriptionCommission,
                      )}
                    />
                    <CardStatistics
                      title={dict["Commissions Paid"]}
                      value={formatCurrency(
                        Number(totalSubscriptionCommissionsPaid ?? 0),
                        currencySubscriptionCommission,
                      )}
                    />
                    <CardStatistics
                      title={dict["Pending Commissions"]}
                      value={formatCurrency(
                        Number(totalSubscriptionCommissionsPending ?? 0),
                        currencySubscriptionCommission,
                      )}
                    />
                  </BoxColumns>
                </FormGroup>
                <FormGroup>
                  <SmallTitleModal Title={dict.Use} />
                  <DataTable
                    data={registerUsageSubscriptionCommissions}
                    columns={UsageSubscription}
                    filterColumn="date"
                    dict={dict}
                    currentPage={currentPagerPagesUsageSubscription}
                    totalPages={totalPagesUsageSubscription}
                    itemsPerPage={itemsPerPagerUsageSubscription}
                    handlePageChange={handlePageChangePagesUsageSubscription}
                  />
                </FormGroup>
                <ButtonComponent
                  variant="success"
                  widthFull
                  text={
                    isSettling || isSubmittingSubscriptionCommission
                      ? `${dict.Sending}...`
                      : `${dict["Write Off"]}`
                  }
                  type="button"
                  disabled={isSettling || isSubmittingSubscriptionCommission}
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                  onClick={async () => {
                    // Set local settlement mode for UI state
                    setSubmitTypeSubscriptionCommission(true);
                    setSettleMode("subscription");

                    clearErrorsSubscriptionCommission?.();

                    // compute date range from selected month or last search
                    let start: Date | null = null;
                    let end: Date | null = null;

                    if (selectedMonth) {
                      const monthRange = getMonthDateRange(selectedMonth);
                      if (monthRange.startDate && monthRange.endDate) {
                        start = new Date(monthRange.startDate);
                        end = new Date(monthRange.endDate);
                      }
                    } else if (
                      searchDataSubscriptionCommission?.startDate &&
                      searchDataSubscriptionCommission?.endDate
                    ) {
                      start = new Date(
                        searchDataSubscriptionCommission.startDate,
                      );
                      end = new Date(searchDataSubscriptionCommission.endDate);
                    }

                    if (!start || !end) {
                      setErrorSubscriptionCommission("startDate", {
                        type: "manual",
                        message:
                          "Por favor, selecione um mês ou realize uma busca antes de dar baixa.",
                      } as any);
                      focusField("selectedMonth");
                      return;
                    }

                    // set start/end into the form (keep UI in sync)
                    setValueSubscriptionCommission?.("startDate", start as any);
                    setValueSubscriptionCommission?.("endDate", end as any);

                    // read current form values to validate manually before settlement
                    const formValues =
                      getValuesSubscriptionCommission?.() || ({} as any);

                    // validate required settlement fields
                    if (!formValues.payment_method_id) {
                      setErrorSubscriptionCommission("payment_method_id", {
                        type: "manual",
                        message: "Por favor, selecione o método de pagamento",
                      } as any);
                      focusField("payment_method_id");
                      return;
                    }

                    if (!formValues.paid_at) {
                      setErrorSubscriptionCommission("paid_at", {
                        type: "manual",
                        message: "Por favor, preencha a data de pagamento",
                      } as any);
                      focusField("paid_at");
                      return;
                    }

                    // construct submission payload merging computed dates + form fields
                    const dataToSubmit = {
                      ...(formValues || {}),
                      startDate: start,
                      endDate: end,
                    };

                    // directly call local handler for settlement (bypassing RHF submit race)
                    await handleLocalSubscriptionSubmit(dataToSubmit, true);
                  }}
                />
              </>
            )}
          </form>
        </div>
      </TabsContent>
      <TabsContent value="normal-service">
        <form
          className="space-y-4"
          onSubmit={handleSubmitNormalCommission(handleNormalCommissionSearch)}
        >
          <BoxAlignItems gap="15px">
            <FormGroup>
              <Input
                {...registerNormalCommission("startDate")}
                type="date"
                label={dict["Start Date"]}
              />
              {errorsNormalCommission.startDate && (
                <p className="text-red-500 text-sm">
                  {errorsNormalCommission.startDate.message}
                </p>
              )}
            </FormGroup>
            <FormGroup>
              <Input
                {...registerNormalCommission("endDate")}
                type="date"
                label={dict["End Date"]}
              />
              {errorsNormalCommission.endDate && (
                <p className="text-red-500 text-sm">
                  {errorsNormalCommission.endDate.message}
                </p>
              )}
            </FormGroup>
          </BoxAlignItems>
          <FormGroup>
            <LabelInput labelInput={dict.Employee} />
            <Select
              {...registerNormalCommission("employee_id")}
              options={EmployeeOptions}
            />
            {errorsNormalCommission.employee_id && (
              <p className="text-red-500 text-sm">
                {errorsNormalCommission.employee_id.message}
              </p>
            )}
          </FormGroup>
          <FormGroup>
            <ButtonComponent
              icon={<Search />}
              text={dict["Search"]}
              disabled={isSubmittingNormalCommission}
              onClick={() => {
                setSubmitTypeNormalCommission(false);
                handleSubmitNormalCommission(handleNormalCommissionSearch)();
              }}
              type="button"
              widthFull
              variant="success"
            />
          </FormGroup>
          <FormGroup>
            <Input
              {...registerNormalCommission("paid_at", {
                required: "Data de pagamento é obrigatória",
              })}
              name="paid_at"
              type="date"
              label={`${dict["Payment Date"]} *`}
            />
            {errorsNormalCommission.paid_at && (
              <p className="text-red-500 text-sm">
                {errorsNormalCommission.paid_at.message}
              </p>
            )}
          </FormGroup>
          <FormGroup>
            <LabelInput labelInput={`${dict["Payment method"]} *`} />
            <Select
              {...registerNormalCommission("payment_method_id", {
                required: "Método de pagamento é obrigatório",
              })}
              options={paymentMethodOptions}
            />
            {errorsNormalCommission.payment_method_id && (
              <p className="text-red-500 text-sm">
                {errorsNormalCommission.payment_method_id.message}
              </p>
            )}
          </FormGroup>
          {hasSearched && (
            <>
              <FormGroup>
                <CardStatistics
                  title={dict["Normal commission pending"]}
                  value={formatCurrency(
                    Number(
                      totalPendingNormalCommission?.total_commissions_pending ??
                        0,
                    ),
                    currencyNormalCommission,
                  )}
                />
              </FormGroup>
              <FormGroup>
                <BoxColumns columns={2}>
                  <CardStatistics
                    title={dict["Pending purchases"]}
                    value={formatCurrency(
                      Number(
                        totalPendingNormalCommission?.total_purchases_pending ??
                          0,
                      ),
                      currencyNormalCommission,
                    )}
                  />
                  <CardStatistics
                    title={dict["Outstanding vouchers"]}
                    value={formatCurrency(
                      pendingVouchersTotal,
                      currencyNormalCommission,
                    )}
                  />
                </BoxColumns>
              </FormGroup>
              <FormGroup>
                <CardStatistics
                  title={dict["Outstanding payment to employee"]}
                  value={formatCurrency(
                    outstandingPaymentToEmployeeTotal,
                    currencyNormalCommission,
                  )}
                />
              </FormGroup>
              <FormGroup>
                <SmallTitleModal Title={dict["Vouchers and Purchases"]} />
                <DataTable
                  data={registerPurchaseVouchersNC}
                  columns={columnsDataPurchaseVouchers}
                  filterColumn="date"
                  dict={dict}
                  currentPage={currentPagerPurchaseVouchers}
                  totalPages={totalPagesrPurchaseVouchers}
                  itemsPerPage={itemsPerPagerPurchaseVouchers}
                  handlePageChange={handlePageChangePurchaseVouchers}
                />
              </FormGroup>
              <FormGroup>
                <SmallTitleModal Title={dict["Commissions"]} />
                <DataTable
                  data={registerPendingNormalCommission}
                  columns={columnsDataPendingNormalCommission}
                  filterColumn="date"
                  dict={dict}
                  currentPage={currentPagerPendingNormalCommission}
                  totalPages={totalPagesrPendingNormalCommission}
                  itemsPerPage={itemsPerPagerPendingNormalCommission}
                  handlePageChange={handlePageChangePendingNormalCommission}
                />
              </FormGroup>
              {/* <FormGroup>
                                <BoxAlignItems></BoxAlignItems>
                            </FormGroup> */}
              <ButtonComponent
                variant="success"
                widthFull
                text={
                  isSubmittingNormalCommission
                    ? `${dict.Sending}...`
                    : `${dict["Write Off"]}`
                }
                type="button"
                disabled={isSubmittingNormalCommission}
                className="bg-blue-500 text-white px-4 py-2 rounded"
                onClick={async () => {
                  // Primeiro realizar baixa das comissões (settlement)
                  setSubmitTypeNormalCommission(true);
                  setSettleMode("normal");
                  await handleSubmitNormalCommission((data: any) =>
                    handleLocalNormalSubmit(data, true),
                  )();
                  // Em seguida descontar vales e compras pendentes
                  await handleDiscountVouchers();
                }}
              />
            </>
          )}
        </form>
      </TabsContent>
    </S.TabContainer>
  );
};

export default TabDownloadCommission;
